/**
 * Write a permission tuple. Five fields instead of two free-text refs:
 * object type → object → relation → subject type → subject.
 *
 * WRITE-ONLY by design. Deletion lives on the list, behind a confirm dialog
 * and next to the row being removed; a radio on a create form was one mis-click
 * from revoking access with no confirmation step.
 *
 * Userset subjects (`user:x#member`) are deliberately not offered: every tuple
 * lookup in `authz/engine.ts` requires `subject_relation IS NULL`, so such a
 * tuple is never consulted by any check. Offering the control would only let an
 * operator create a grant that grants nothing — the P-41.3 failure mode.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { RelationBody } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { hub } from '@/lib/hub-client';
import {
  relationAfterTypeChange,
  relationOptionsFor,
  useNamespaceRelations,
} from './namespace-relations';

/** One side of the tuple as the form holds it: an id plus its human label. */
export interface RefDraft {
  type: string;
  id: string;
  label: string;
}

const EMPTY_REF: RefDraft = { type: '', id: '', label: '' };

/** `type:id`, or '' while either half is missing (exported test seam). */
export function toRef(draft: RefDraft): string {
  return draft.type && draft.id ? `${draft.type}:${draft.id}` : '';
}

/**
 * What is still missing before the tuple can be written (exported test seam).
 * Returns null when it is complete — the Write button reads exactly this, so
 * the disabled state and the message can never disagree.
 */
export function missingPiece(object: RefDraft, relation: string, subject: RefDraft): string | null {
  if (!object.type) return 'Pick an object type.';
  if (!object.id) return 'Pick an object.';
  if (!relation) return 'Pick a relation.';
  if (!subject.type) return 'Pick a subject type.';
  if (!subject.id) return 'Pick a subject.';
  return null;
}

/** One `type → entity` pair. Shared by both sides — they behave identically. */
function RefPicker({
  side,
  draft,
  onChange,
  objectTypes,
  typeIsClosed,
}: {
  side: 'object' | 'subject';
  draft: RefDraft;
  onChange: (next: RefDraft) => void;
  objectTypes: string[];
  /** Object types are validated server-side; subject types are free-form. */
  typeIsClosed: boolean;
}) {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [searchable, setSearchable] = useState(true);
  const [loading, setLoading] = useState(false);

  const search = useCallback(
    async (term: string) => {
      if (!draft.type) return;
      setLoading(true);
      try {
        const res = await hub.relationsObjects({ query: { type: draft.type, q: term, limit: 20 } });
        setSearchable(res.searchable);
        setOptions(res.objects.map((o) => ({ value: o.id, label: o.label })));
      } catch {
        // Leave the last options; the raw-value row still gets the operator through.
        setSearchable(false);
      } finally {
        setLoading(false);
      }
    },
    [draft.type],
  );

  // Re-seed the list whenever the type changes, so the picker opens populated.
  useEffect(() => {
    setOptions([]);
    if (draft.type) void search('');
  }, [draft.type, search]);

  const label = side === 'object' ? 'Object' : 'Subject';
  return (
    <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
      <div className="space-y-2">
        <Label htmlFor={`${side}-type`}>{label} type</Label>
        {typeIsClosed ? (
          <Select
            id={`${side}-type`}
            aria-label={`${label} type`}
            className="w-full"
            value={draft.type}
            onChange={(e) => onChange({ ...EMPTY_REF, type: e.target.value })}
          >
            <option value="" disabled>
              Select a type
            </option>
            {objectTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        ) : (
          // Subject types are NOT validated by the hub (`estimate:<id>` is a
          // legal subject), so the namespaces are suggestions, not a fence.
          <Combobox
            id={`${side}-type`}
            aria-label={`${label} type`}
            value={draft.type}
            options={objectTypes.map((t) => ({ value: t, label: t }))}
            placeholder="Select a type"
            searchPlaceholder="Search or type…"
            allowRawValue
            onSelect={(value) => onChange({ ...EMPTY_REF, type: value })}
          />
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor={side}>{label}</Label>
        {searchable ? (
          <Combobox
            id={side}
            aria-label={label}
            value={draft.id}
            valueLabel={draft.label}
            options={options}
            loading={loading}
            disabled={!draft.type}
            placeholder={draft.type ? `Search ${draft.type}…` : 'Pick a type first'}
            searchPlaceholder="Search by name or id…"
            allowRawValue
            onSearch={(term) => void search(term)}
            onSelect={(id, optionLabel) => onChange({ ...draft, id, label: optionLabel })}
          />
        ) : (
          // Unlistable namespace (`system`, `audit_log`, `chunk`) or a type the
          // hub does not know — the raw id is still a legal ref.
          <Input
            id={side}
            aria-label={label}
            placeholder="Id"
            value={draft.id}
            onChange={(e) => onChange({ ...draft, id: e.target.value, label: '' })}
          />
        )}
        {draft.id && (
          <code className="block font-mono text-[11px] text-muted-foreground">{toRef(draft)}</code>
        )}
      </div>
    </div>
  );
}

export function RelationsCreate() {
  const navigate = useNavigate();
  const namespaceRelations = useNamespaceRelations();
  const [object, setObject] = useState<RefDraft>(EMPTY_REF);
  const [subject, setSubject] = useState<RefDraft>(EMPTY_REF);
  const [relation, setRelation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const objectTypes = useMemo(() => Object.keys(namespaceRelations).sort(), [namespaceRelations]);
  const relationOptions = useMemo(
    () => relationOptionsFor(namespaceRelations, object.type),
    [namespaceRelations, object.type],
  );

  const onObject = (next: RefDraft) => {
    setObject(next);
    if (next.type !== object.type) {
      setRelation((current) =>
        relationAfterTypeChange(relationOptionsFor(namespaceRelations, next.type), current),
      );
    }
  };

  const missing = missingPiece(object, relation, subject);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (missing) return setError(missing);

    const body: RelationBody = { object: toRef(object), relation, subject: toRef(subject) };
    setBusy(true);
    try {
      await hub.relationsWrite({ body });
      setSuccess(`Granted ${relation} on ${object.label || toRef(object)} to ${subject.label || toRef(subject)}.`);
      setObject(EMPTY_REF);
      setSubject(EMPTY_REF);
      setRelation('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Grant permission</h1>
        <p className="text-sm text-muted-foreground">
          Write a tuple: who gets which relation on which object. Revoking is done from the list.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tuple</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={submit}>
            <RefPicker
              side="object"
              draft={object}
              onChange={onObject}
              objectTypes={objectTypes}
              typeIsClosed={objectTypes.length > 0}
            />
            <div className="space-y-2 sm:max-w-[12rem]">
              <Label htmlFor="relation">Relation</Label>
              {relationOptions.length > 0 ? (
                <Select
                  id="relation"
                  aria-label="Relation"
                  className="w-full"
                  value={relation}
                  // Locked until the object type is known: the offered set is
                  // scoped to it, so choosing first only to have the choice
                  // cleared on the next click is worse than not offering.
                  disabled={!object.type}
                  onChange={(e) => setRelation(e.target.value)}
                >
                  <option value="" disabled>
                    Select a relation
                  </option>
                  {relationOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  id="relation"
                  aria-label="Relation"
                  placeholder={object.type ? `no direct relations on ${object.type}` : 'Pick an object type first'}
                  value={relation}
                  onChange={(e) => setRelation(e.target.value)}
                />
              )}
              {object.type && relationOptions.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Direct relations on <code>{object.type}</code> only — computed ones (e.g.{' '}
                  <code>{object.type}#read</code>) are never consulted by authz checks.
                </p>
              )}
            </div>
            <RefPicker
              side="subject"
              draft={subject}
              onChange={setSubject}
              objectTypes={objectTypes}
              typeIsClosed={false}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            {success && <p className="text-sm text-foreground">{success}</p>}
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={busy || missing !== null}>
                {busy ? 'Writing…' : 'Grant'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/permissions')}>
                Done
              </Button>
              {missing && <span className="text-sm text-muted-foreground">{missing}</span>}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
